import { memo, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, Typography, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';

import { drawerWidth } from '@/store/constant';
import { useDispatch, useSelector } from '@/store';
import { openDrawer } from '@/store/slices/menu';

const VERSION_BAR_HEIGHT = 40;

// ==============================|| SIDEBAR DRAWER ||============================== //

const logo = (
    <Box sx={{ display: 'flex', p: 2 }}>
        <LogoSection />
    </Box>
);

const versionLabel = (
    <Box
        sx={{
            height: VERSION_BAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            px: 2
        }}
    >
        <Typography variant="caption" color="text.secondary">
            v{__APP_VERSION__}
        </Typography>
    </Box>
);

const Sidebar = () => {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { drawerOpen } = useSelector(({ menu }) => menu);

    // Match original Berry heights; when open, reserve space for the version bar
    const menuHeight = matchUpMd
        ? `calc(100vh - ${88 + (drawerOpen ? VERSION_BAR_HEIGHT : 0)}px)`
        : `calc(100vh - ${56 + (drawerOpen ? VERSION_BAR_HEIGHT : 0)}px)`;

    const drawer = useMemo(
        () =>
            drawerOpen ? (
                <PerfectScrollbar
                    component="div"
                    options={{ suppressScrollX: true }}
                    style={{
                        height: menuHeight,
                        paddingTop: '30px',
                        paddingLeft: '16px',
                        paddingRight: '16px'
                    }}
                >
                    <MenuList />
                </PerfectScrollbar>
            ) : (
                <Box
                    sx={{
                        height: menuHeight,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        mt: 2.5
                    }}
                >
                    <MenuList />
                </Box>
            ),
        [menuHeight, drawerOpen]
    );

    return (
        <Box component="nav" sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }} aria-label="drawer container">
            {matchDownMd ? (
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => dispatch(openDrawer(!drawerOpen))}
                    sx={{
                        '& .MuiDrawer-paper': {
                            mt: matchDownMd ? 0 : 11,
                            zIndex: 1090,
                            width: drawerWidth,
                            background: theme.palette.background.default,
                            color: theme.palette.text.primary,
                            borderRight: 'none',
                            overflowX: 'hidden'
                        }
                    }}
                    ModalProps={{ keepMounted: true }}
                    color="inherit"
                >
                    {matchDownMd && logo}
                    {drawer}
                    {versionLabel}
                </Drawer>
            ) : (
                <MiniDrawerStyled variant="permanent" open={drawerOpen}>
                    {logo}
                    {drawer}
                    {versionLabel}
                </MiniDrawerStyled>
            )}
        </Box>
    );
};

export default memo(Sidebar);
